/**
 * Role-to-route visibility matrix
 *
 * Maps each EmployeeRole to the route path prefixes they are allowed to access.
 * Used by route guards and sidebar filtering.
 *
 * INTEGRATION NOTE: This config is intentionally decoupled from enforcement.
 * Wire `hasRouteAccess` into `ProtectedRoute` (or a `RoleRoute` wrapper) and
 * filter `getMenuItems` in `MainLayout` using the current user's role.
 * See follow-up tasks for route-guard and sidebar integration.
 *
 * REPORT ROUTES: Report-level access is controlled via report-access.ts config.
 * The REPORTS_DASHBOARD route is granted to roles that have at least one report.
 */
import { type EmployeeRole, EMPLOYEE_ROLES } from '../types/employee.types';
import {
  REPORT_ROUTES,
  REPORT_ACCESS_ROLES,
  type ReportType,
} from '../../features/reports/config/report-access';

/**
 * Route path prefix groups, derived from App.tsx route definitions.
 */
const MASTER_DATA_ROUTES = [
  '/materials',
  '/customers',
  '/suppliers',
  '/warehouses',
  '/departments',
  '/employees',
  '/categories',
  '/units-of-measure',
] as const;

const INVENTORY_ROUTES = [
  '/inbound-documents',
  '/outbound-documents',
  '/counting-documents',
  '/inventory-alerts',
  '/inventory-balances',
] as const;

const SALES_ROUTES = [
  '/sales-orders',
  '/shipments',
  '/quotations',
  '/price-lists',
  '/special-price-requests',
  '/returns',
  '/statements',
] as const;

/** Individual sales route paths used for partial-module grants */
const SALES_SHIPMENTS_ROUTE = '/shipments' as const;
const SALES_STATEMENTS_ROUTE = '/statements' as const;

const PROCUREMENT_ROUTES = [
  '/purchase-orders',
  '/purchase-receipts',
  '/purchase-returns',
] as const;

/**
 * Production routes.
 * NOTE: The prefix `/production` grants access to ALL production sub-routes
 * (e.g. `/production/work-orders`, `/production/boms`). The matrix grants
 * PRODUCTION_WORKER full Production prefix access per the approved spec.
 */
const PRODUCTION_ROUTES = ['/production'] as const;

/**
 * Documents module (module 9): standardized print output.
 * Template management mirrors the backend DOCUMENTS_ADMIN_ROLES
 * (SYSTEM_ADMIN only); print preview mirrors the sales read grant so the
 * print surface is exactly as wide as the detail pages linking into it.
 */
const DOCUMENTS_TEMPLATE_ROUTES = ['/documents/templates'] as const;
const PRINT_ROUTES = [
  '/print/sales-order',
  '/print/shipment',
  '/print/quotation',
] as const;

const FINANCE_ROUTES = [
  '/receivables',
  '/payables',
  '/receipt-vouchers',
  '/payment-vouchers',
  '/aging-reports',
] as const;

/**
 * Reports dashboard route - accessible to anyone with at least one report
 */
const REPORTS_DASHBOARD = '/reports' as const;

/**
 * Helper to get report routes for a specific role based on REPORT_ACCESS_ROLES
 */
function getReportRoutesForRole(role: EmployeeRole): readonly string[] {
  const reportTypes = Object.keys(REPORT_ACCESS_ROLES) as ReportType[];
  const routes: string[] = [];

  for (const reportType of reportTypes) {
    if (REPORT_ACCESS_ROLES[reportType].includes(role)) {
      routes.push(REPORT_ROUTES[reportType]);
    }
  }

  // Add dashboard if role has any report access
  if (routes.length > 0) {
    routes.unshift(REPORTS_DASHBOARD);
  }

  return routes;
}

/**
 * Approved role-to-module access matrix:
 *
 * SYSTEM_ADMIN:       All modules (User Management admin routes removed in Goal-028 M4)
 * SALESPERSON:        Master Data, Sales (all), Reports (sales, fulfillment)
 * SALES_MANAGER:      Same surfaces as SALESPERSON plus pricing management (013 spec §5)
 * WAREHOUSE_KEEPER:   Master Data, Inventory, Sales (shipments only), Production (all sub-routes), Reports (inventory, fulfillment)
 * PURCHASER:          Master Data, Procurement, Inventory (all sub-routes, view)
 * PRODUCTION_PLANNER: Master Data, Production (all sub-routes), Inventory (all sub-routes, view), Reports (production)
 * PRODUCTION_WORKER:  Production (all sub-routes via /production prefix)
 * FINANCE_STAFF:      Master Data, Finance, Sales (statements only), Reports (AR/AP aging)
 *
 * Documents module 9: template management is SYSTEM_ADMIN-only; print
 * preview (/print/*) follows the sales read grant (see DOCUMENTS_TEMPLATE_ROUTES
 * and PRINT_ROUTES below).
 */
export const ROLE_ROUTE_MAP: Readonly<Record<EmployeeRole, readonly string[]>> =
  Object.freeze({
    SYSTEM_ADMIN: [
      ...MASTER_DATA_ROUTES,
      ...INVENTORY_ROUTES,
      ...SALES_ROUTES,
      ...PROCUREMENT_ROUTES,
      ...PRODUCTION_ROUTES,
      ...FINANCE_ROUTES,
      ...DOCUMENTS_TEMPLATE_ROUTES,
      ...PRINT_ROUTES,
      REPORTS_DASHBOARD,
      ...Object.values(REPORT_ROUTES),
    ],

    SALESPERSON: [
      ...MASTER_DATA_ROUTES,
      ...SALES_ROUTES,
      ...PRINT_ROUTES,
      ...getReportRoutesForRole('SALESPERSON'),
    ],

    // 013 pricing governance: sales managers see the same sales surfaces as
    // salespeople plus the manager-only pricing management views (costing
    // detail, request review, rate maintenance arrive with later slices).
    SALES_MANAGER: [
      ...MASTER_DATA_ROUTES,
      ...SALES_ROUTES,
      ...PRINT_ROUTES,
      ...getReportRoutesForRole('SALESPERSON'),
    ],

    // Sees all of Inventory; Sales shipments only; all Production sub-routes
    WAREHOUSE_KEEPER: [
      ...MASTER_DATA_ROUTES,
      ...INVENTORY_ROUTES,
      SALES_SHIPMENTS_ROUTE,
      ...PRODUCTION_ROUTES,
      ...PRINT_ROUTES,
      ...getReportRoutesForRole('WAREHOUSE_KEEPER'),
    ],

    // Sees all of Inventory (view); full Procurement
    PURCHASER: [
      ...MASTER_DATA_ROUTES,
      ...PROCUREMENT_ROUTES,
      ...INVENTORY_ROUTES,
    ],

    // Sees all of Inventory (view); all Production sub-routes
    PRODUCTION_PLANNER: [
      ...MASTER_DATA_ROUTES,
      ...PRODUCTION_ROUTES,
      ...INVENTORY_ROUTES,
      ...getReportRoutesForRole('PRODUCTION_PLANNER'),
    ],

    // All Production sub-routes via /production prefix.
    // No report routes: the requirements permission matrix grants workers no
    // report access (M7 row "-"), enforced by the backend since 2026-10-10.
    PRODUCTION_WORKER: [
      ...PRODUCTION_ROUTES,
    ],

    // Finance, Sales statements only, Reports, Master Data
    FINANCE_STAFF: [
      ...MASTER_DATA_ROUTES,
      ...FINANCE_ROUTES,
      SALES_STATEMENTS_ROUTE,
      ...PRINT_ROUTES,
      ...getReportRoutesForRole('FINANCE_STAFF'),
    ],
  });

/**
 * Checks whether a given role is allowed to access a route path.
 *
 * Matching is prefix-based: a role with access to `/production` can also access
 * `/production/work-orders/123`. This mirrors how React Router nested routes work.
 *
 * @param role - The employee's role
 * @param path - The current route pathname (e.g. `/sales-orders/42`)
 * @returns `true` if the role has access; `false` otherwise
 */
export function hasRouteAccess(role: EmployeeRole, path: string): boolean {
  const allowedPrefixes = ROLE_ROUTE_MAP[role];
  return allowedPrefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

/**
 * Derives all roles that are allowed to access a given route path.
 *
 * This is the inverse of hasRouteAccess - used to generate the allowedRoles
 * array for RoleRoute wrappers, ensuring ROLE_ROUTE_MAP is the single source
 * of truth for route-level access control.
 *
 * @param path - The route path (e.g. `/materials`, `/production/work-orders`)
 * @returns Array of EmployeeRole values that have access to the path
 */
export function getRolesForRoute(path: string): EmployeeRole[] {
  return EMPLOYEE_ROLES.filter((role) => hasRouteAccess(role, path));
}

/**
 * Gets the default landing route for a given role.
 *
 * Returns the first allowed route from the role's route map, ensuring
 * authenticated users always land on a route they can access.
 * This replaces the legacy hard-coded `/materials` fallback.
 *
 * IMPORTANT: This function should only be called for authenticated users.
 * For unauthenticated redirects, always use `/login`.
 *
 * @param role - The employee's role
 * @returns The first allowed route path for the role
 *
 * @example
 * // PRODUCTION_WORKER lands on /production instead of /materials
 * getDefaultRouteForRole('PRODUCTION_WORKER') // '/production'
 *
 * // FINANCE_STAFF lands on their first allowed route
 * getDefaultRouteForRole('FINANCE_STAFF') // '/materials'
 */
export function getDefaultRouteForRole(role: EmployeeRole): string {
  const allowedRoutes = ROLE_ROUTE_MAP[role];
  // ROLE_ROUTE_MAP is guaranteed non-empty for all valid roles
  return allowedRoutes[0];
}
